import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import ReviewForm from "../modules/review/components/review-form";
import Container from "@/components/ui/container";
import { useReviews } from "@/modules/review/hooks/use-reviews";
import { useAuth } from "@/modules/auth";
import { useNavigationGuard } from "@/contexts/navigation-guard-context";
import type { ReviewResponse } from "@/modules/review/types";

export default function EditReviewPage() {
	const navigate = useNavigate();
	const { brandUrl, reviewId } = useParams();
	const { currentWorkspaceId } = useAuth();
	const { showReview } = useReviews();
	const { confirmNavigation } = useNavigationGuard();

	const [review, setReview] = useState<ReviewResponse | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchReview = async () => {
			if (!currentWorkspaceId || !reviewId) return;

			setIsLoading(true);
			setError(null);

			try {
				const result = await showReview(currentWorkspaceId, reviewId);
				if (result) {
					setReview(result);
				} else {
					setError("Review not found");
				}
			} catch (err) {
				setError("Failed to load review");
			} finally {
				setIsLoading(false);
			}
		};

		fetchReview();
	}, [currentWorkspaceId, reviewId, showReview]);

	const handleSuccess = () => {
		navigate(`/${brandUrl}/reviews`);
	};

	const handleCancel = () => {
		if (confirmNavigation()) {
			navigate(`/${brandUrl}/reviews`);
		}
	};

	if (isLoading) {
		return (
			<Container className="py-12">
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="text-tertiary">Loading review...</div>
				</div>
			</Container>
		);
	}

	if (error || !review) {
		return (
			<Container className="py-12">
				<div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
					<div className="text-red-500">{error || "Review not found"}</div>
					<button onClick={() => navigate(`/${brandUrl}/reviews`)} className="text-brand hover:underline">
						Back to reviews
					</button>
				</div>
			</Container>
		);
	}

	return (
		<Container className="py-12">
			{/* Page Header */}
			<div className="mb-8 flex items-center justify-between">
				<div className="flex items-center gap-4">
					<button onClick={handleCancel} className="flex items-center gap-2 text-tertiary hover:text-primary transition-colors">
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
						</svg>
						<span>Back</span>
					</button>
					<div className="w-px h-6 bg-gray-200" />
					<div>
						<h1 className="text-xl font-semibold text-primary">Edit Review</h1>
						<p className="mt-1 text-sm text-tertiary">Update the details of this review.</p>
					</div>
				</div>
			</div>

			<ReviewForm onSuccess={handleSuccess} onCancel={handleCancel} editMode={true} initialData={review} />
		</Container>
	);
}
